import React, { useEffect, useMemo, useRef, useState } from "react";

// Loads a script tag
async function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`head > script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

// Ensure Google Maps JS API is available
async function ensureGoogleMaps({
  apiKey,
  libraries = ["marker", "places", "geometry"],
}) {
  if (window.google?.maps) return;
  const src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries.join(",")}`;
  await loadScript(src);
}

// Ensure MarkerClusterer UMD build is available (window.markerClusterer)
async function ensureMarkerClusterer() {
  if (window.markerClusterer?.MarkerClusterer) return;
  await loadScript(
    "https://unpkg.com/@googlemaps/markerclusterer@2.0.15/dist/index.umd.js",
  );
}

// --- Utility mappers -------------------------------------------------------------
function toLatLng(item) {
  // The API returns Latitude/Longitude as strings; guard + parse.
  const lat = parseFloat(item?.Latitude);
  const lng = parseFloat(item?.Longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}

function agentColor(item) {
  const t = item?.AgentType;
  return t === "I" ? "purple" : "teal";
}

function formatAddressFromItem(item = {}) {
  const a1 = item?.MailingAddress?.Address1 ?? "";
  const a2 = item?.MailingAddress?.Address2 ?? "";
  const city = item?.MailingAddress?.City ?? "";
  const st = item?.MailingAddress?.State ?? "";
  const zip = item?.MailingAddress?.Zip ?? "";

  return [
    [a1, a2].filter(Boolean).join(" "),
    [city, st].filter(Boolean).join(", "),
    zip,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function buildGoogleMapsUrlFromItem(item = {}, mode = "search") {
  const addr = formatAddressFromItem(item) || item?.OfficeDisplayName || "";
  return `https://www.google.com/maps/${mode}/?api=1&query=${encodeURIComponent(addr)}`;
}

function buildInfoContent(
  item,
  officeType,
  phoneLabel,
  faxLabel,
  mapLabel,
  detailsLabel,
) {
  const isStewartOwned = item?.StewartOwned === "true";
  const mapUrl = buildGoogleMapsUrlFromItem(item, "search");
  const badgeText = isStewartOwned
    ? officeType?.find(t => t.key === "stewart")?.label || "Stewart Office"
    : officeType?.find(t => t.key === "agency")?.label || "Independent Agency";

  return `
  <article class="office-card ${isStewartOwned ? "is-stewart" : "is-independent"}">
    <div class="office-card__badge">
      <span class="office-card__badge-icon" aria-hidden="true"
        ><i class="${isStewartOwned ? "fal fa-building" : "far fa-male"}"></i></span
      ><span class="office-card__badge-text">${badgeText}</span>
    </div>
    <header class="office-card__header">
      <h3 class="office-card__title">
        ${item.OfficeDisplayName} - ${item.City}
      </h3>
    </header>
    <div class="office-card__content">
      <div class="office-card__address">
        <span
          class="office-card__street"
          href="#"
          target="_blank"
          rel="noreferrer"
          >${item.MailingAddress.Address1}</span
        >
        <div class="office-card__line2">${item.MailingAddress.Address2}</div>
        <div class="office-card__city">${item.MailingAddress.City}, ${item.MailingAddress.State} ${item.MailingAddress.Zip}</div>
      </div>
      <div class="office-card__contact">
        <div class="office-card__row">
          <span class="office-card__label">${phoneLabel ?? "ph."}</span
          ><span class="office-card__phone">${item.Phone ?? "N/A"}</span>
        </div>
        <div class="office-card__row">
          <span class="office-card__label">${faxLabel ?? "fx."}</span
          ><span class="office-card__fax">${item.Fax ?? "N/A"}</span>
        </div>
      </div>
    </div>
    <footer class="office-card__footer">
      <div class="office-card__action-container">
        <div class="office-card__action">
          <i class="fal fa-map-marker-alt office-card__fa" aria-hidden="true"></i
          ><a
            class="office-card__action office-card__action--map"
            href="${mapUrl}"
            target="_blank"
            rel="noreferrer"
            ><span>${mapLabel ?? "Map"}</span></a
          >
        </div>
        <a class="office-card__action" href="#" target="_blank" rel="noreferrer"
          >${detailsLabel ?? "Details"}</a
        >
      </div>
    </footer>
  </article>
  `;
}

// --- The Map component -----------------------------------------------------------
export default function MapLocations({
  locations = [],
  apiKey,
  mapId = "df8d2d0783111f6decedefb3",
  cluster = true,
  fitToMarkers = true,
  minZoom = 3,
  maxZoom = 15,
  initialCenter = { lat: 39.8283, lng: -98.5795 },
  initialZoom = 5,
  officeType,
  phoneLabel,
  faxLabel,
  mapLabel,
  detailsLabel,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const infoRef = useRef(null);
  const markersRef = useRef([]);
  const clusterRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Load Google Maps + Clusterer once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureGoogleMaps({ apiKey });
        await ensureMarkerClusterer();
        if (!cancelled) setReady(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load Google Maps scripts", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Initialize the map exactly once when ready
  useEffect(() => {
    if (!ready || mapRef.current || !containerRef.current) return;

    const map = new google.maps.Map(containerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      mapId,
      disableDefaultUI: true,
      minZoom,
      maxZoom,
    });
    const info = new google.maps.InfoWindow();

    mapRef.current = map;
    infoRef.current = info;
  }, [ready, initialCenter, initialZoom, mapId, minZoom, maxZoom]);

  // Helper to clear existing markers (and cluster) before re-adding
  function clearMarkers() {
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (clusterRef.current) {
      clusterRef.current.clearMarkers();
      clusterRef.current = null;
    }
  }

  // Update markers whenever `locations` changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove old markers
    clearMarkers();

    // Build new markers
    const bounds = new google.maps.LatLngBounds();
    const freshMarkers = [];

    locations.forEach(item => {
      const pos = toLatLng(item);
      if (!pos) return;

      bounds.extend(pos);
      const marker = new google.maps.Marker({
        position: pos,
        map,
        title: item?.OfficeName || item?.title || "Location",
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: agentColor(item),
          fillOpacity: 1,
          strokeColor: "#FFFFFF",
          strokeWeight: 2,
        },
      });

      marker.addListener("click", () => {
        const html = buildInfoContent(
          item,
          officeType,
          phoneLabel,
          faxLabel,
          mapLabel,
          detailsLabel,
        );
        infoRef.current?.setContent(html);
        infoRef.current?.open({ anchor: marker, map, shouldFocus: false });
      });

      freshMarkers.push(marker);
    });

    markersRef.current = freshMarkers;

    // Apply clustering if requested
    if (
      cluster &&
      window.markerClusterer?.MarkerClusterer &&
      freshMarkers.length > 0
    ) {
      const { MarkerClusterer } = window.markerClusterer;
      clusterRef.current = new MarkerClusterer({
        map,
        markers: freshMarkers,
        algorithmOptions: { maxZoom: 5 },
        renderer: {
          // Custom renderer with a badge
          render({ count, position }) {
            const div = document.createElement("div");
            div.style.background = "rgba(0, 91, 110, 0.8)";
            div.style.color = "#fff";
            div.style.borderRadius = "50%";
            div.style.width = `${40 + Math.log(count) * 10}px`;
            div.style.height = div.style.width;
            div.style.display = "flex";
            div.style.alignItems = "center";
            div.style.justifyContent = "center";
            div.style.fontSize = "14px";
            div.style.fontWeight = "700";
            div.style.boxShadow = "0 0 10px rgba(0,0,0,0.3)";
            div.textContent = String(count);
            return new google.maps.marker.AdvancedMarkerElement({
              position,
              content: div,
            });
          },
        },
      });
    }

    // Fit bounds to markers
    if (fitToMarkers && !bounds.isEmpty()) {
      map.fitBounds(bounds);
      google.maps.event.addListenerOnce(map, "bounds_changed", () => {
        const z = map.getZoom();
        if (z > 10) map.setZoom(10);
        if (z < 4) map.setZoom(4);
      });
    }
  }, [locations, cluster, fitToMarkers]);

  return <div ref={containerRef} className="locator-results-map-container" />;
}
